import React from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Tooltip } from '@/components/Tooltip';
import {
  normalizeEmailValue,
  PRIVACY_REDACTION,
} from '@/utils/statisticValidation.util';

/**
 * Props for EmailCell component using discriminated union for type safety
 */
type EmailCellProps =
  | {
      /** Direct email value */
      value: string | null | undefined;
      /** Entity-based extraction not used */
      entity?: never;
      field?: never;
    }
  | {
      /** Entity object containing email field */
      entity: unknown;
      /** Field name to extract from entity */
      field: string;
      /** Value not used when entity provided */
      value?: never;
    };

/**
 * Shared email cell component with validation, tooltip, and copy functionality
 *
 * Used by:
 * - Seed table (registrant email column)
 * - QueryBuilder (seed email results)
 *
 * Features:
 * - Email validation (filters invalid/URL values)
 * - Lowercase normalization
 * - Copy-to-clipboard
 * - Tooltip with full value
 * - Accessible (ARIA labels, keyboard navigation)
 *
 * @example
 * // Direct value
 * <EmailCell value="user@example.com" />
 *
 * @example
 * // Entity extraction
 * <EmailCell entity={seed} field="email" />
 */
export const EmailCell: React.FC<EmailCellProps> = React.memo(props => {
  // Extract email value using discriminated union
  const emailValue =
    'value' in props
      ? props.value
      : ((props.entity as Record<string, unknown> | null | undefined)?.[
          props.field
        ] as string | null | undefined);

  // Privacy-redacted contacts: the backend persists a sentinel string when a
  // privacy registry (e.g. GDPR / WHOIS privacy) hides the registrant contact.
  // Surface it as muted text (not an email, not copyable) before normalization,
  // which would otherwise reject it and render '-'.
  if (
    typeof emailValue === 'string' &&
    emailValue.trim().toLowerCase() === PRIVACY_REDACTION.toLowerCase()
  ) {
    return (
      <Tooltip
        title="The registry redacted this contact behind a privacy service (e.g. GDPR / WHOIS privacy)"
        placement="bottom"
      >
        <span
          className="text-chariot-text-secondary text-sm"
          aria-label="Contact redacted by privacy service"
        >
          {PRIVACY_REDACTION}
        </span>
      </Tooltip>
    );
  }

  // Validate and normalize
  const normalized = normalizeEmailValue(emailValue);

  // Return dash for invalid/missing emails
  if (!normalized) {
    return (
      <span
        className="text-chariot-text-secondary text-sm"
        aria-label="No email available"
      >
        -
      </span>
    );
  }

  // Main content
  const content = (
    <span
      className="text-chariot-text-primary text-sm"
      aria-label={`Email: ${normalized}`}
    >
      {normalized}
    </span>
  );

  // Wrap with tooltip and copy functionality
  return (
    <Tooltip title={normalized} placement="bottom">
      <CopyToClipboard textToCopy={normalized}>
        <span className="hover:bg-chariot-layer1 cursor-pointer rounded px-1 py-0.5">
          {content}
        </span>
      </CopyToClipboard>
    </Tooltip>
  );
});

EmailCell.displayName = 'EmailCell';
